import { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Bug, X, ArrowClockwise } from '@phosphor-icons/react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Floating diagnostics tool for super_admin only.
 * - Panel oculto por defecto; se activa con Ctrl+Shift+D o con el FAB bug icon.
 * - Permite auditar en tiempo real los flags del catálogo SOAP (getViajeFullDetail),
 *   la primera y última ciudad del itinerario (getDiaOrden), y las decisiones
 *   de validate_transfers() (INCLUIDO / SIDE_PRICE / OPTIONAL_EXTRA / UNAVAILABLE)
 *   con los precios reales de getPreciosTraslados.
 */
export default function SoapDiagnosticsPanel() {
  const { isSuperAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [tourId, setTourId] = useState('EM_2500314');
  const [numPaxs, setNumPaxs] = useState(2);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  if (!isSuperAdmin) return null;

  const run = async () => {
    setLoading(true); setErr(null); setData(null);
    try {
      const cleanId = tourId.trim().startsWith('EM_') ? tourId.trim() : `EM_${tourId.trim()}`;
      const res = await axios.get(`${API_URL}/api/tours/${cleanId}/diagnostics`, {
        params: { num_paxs: numPaxs },
        withCredentials: true,
      });
      setData(res.data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const Flag = ({ label, value }) => (
    <div className="flex justify-between items-center text-xs border-b border-border/30 py-1">
      <span className="font-mono text-muted-foreground">{label}</span>
      <span className={`font-bold px-2 py-0.5 rounded ${value === 'S' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
        {value}
      </span>
    </div>
  );

  const Decision = ({ label, value }) => {
    const color = String(value).includes('INCLUIDO') ? 'text-emerald-700 bg-emerald-50'
      : String(value).includes('SIDE_PRICE') ? 'text-blue-700 bg-blue-50'
      : String(value).includes('OPTIONAL_EXTRA') ? 'text-amber-700 bg-amber-50'
      : 'text-rose-700 bg-rose-50';
    return (
      <div className="flex justify-between items-center text-xs py-1">
        <span className="font-mono capitalize">{label}</span>
        <span className={`font-bold px-2 py-0.5 rounded ${color}`}>{value}</span>
      </div>
    );
  };

  return (
    <>
      {/* Floating Action Button — discreet, bottom-right */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-5 left-5 z-50 w-11 h-11 rounded-full bg-pjr-text text-white shadow-lg hover:bg-pjr-blue transition-colors flex items-center justify-center opacity-60 hover:opacity-100"
        title="SOAP Diagnostics (super_admin)"
        data-testid="soap-diagnostics-fab"
      >
        <Bug size={18} weight="bold" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="soap-diagnostics-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug size={20} className="text-pjr-blue" /> Diagnóstico SOAP Europamundo
            </DialogTitle>
            <DialogDescription className="text-xs">
              Auditoría del catálogo, itinerario y decisiones de <code>validate_transfers()</code> en tiempo real.
              Sólo super_admin. No crea reservas.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-end gap-2 mb-4">
            <div className="flex-1">
              <Label className="text-xs">Tour ID</Label>
              <Input value={tourId} onChange={(e) => setTourId(e.target.value)}
                placeholder="EM_2500314" className="h-9"
                data-testid="soap-diag-tour-input" />
            </div>
            <div className="w-24">
              <Label className="text-xs">Pax</Label>
              <Input type="number" min="1" max="9" value={numPaxs}
                onChange={(e) => setNumPaxs(parseInt(e.target.value) || 2)} className="h-9" />
            </div>
            <Button onClick={run} disabled={loading} className="btn-pjr h-9" data-testid="soap-diag-run">
              {loading ? <ArrowClockwise size={14} className="animate-spin" /> : 'Ejecutar'}
            </Button>
          </div>

          {err && (
            <div className="p-3 bg-rose-50 border border-rose-200 rounded text-sm text-rose-800 mb-3" data-testid="soap-diag-error">
              {err}
            </div>
          )}

          {data && (
            <div className="space-y-4">
              {/* Header */}
              <div className="p-3 bg-pjr-bg rounded">
                <p className="font-bold text-sm">{data.name}</p>
                <p className="text-xs text-muted-foreground">
                  {data.europamundo_id} · {data.tour_meta?.duration} días · Serie {data.tour_meta?.serie} · Temporada {data.tour_meta?.season}
                </p>
              </div>

              {/* Catalog Flags */}
              <div className="grid grid-cols-2 gap-x-4 gap-y-0">
                <div className="col-span-2 text-xs font-bold uppercase tracking-wider text-pjr-blue mb-1">
                  getViajeFullDetail — Catalog Flags
                </div>
                {Object.entries(data.catalog_flags || {}).map(([k, v]) => (
                  <Flag key={k} label={k} value={v} />
                ))}
              </div>

              {/* Itinerary */}
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-pjr-blue mb-1">
                  getDiaOrden — Itinerario
                </div>
                <div className="text-xs font-mono bg-slate-50 p-2 rounded space-y-1">
                  <p>Total stops: <b>{data.itinerary?.total_stops}</b></p>
                  <p>First: <b>{data.itinerary?.first_city?.city}</b> (id={data.itinerary?.first_city?.city_id}, {data.itinerary?.first_city?.country})</p>
                  <p>Last: <b>{data.itinerary?.last_city?.city}</b> (id={data.itinerary?.last_city?.city_id}, {data.itinerary?.last_city?.country})</p>
                </div>
              </div>

              {/* Transfer Catalog */}
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-pjr-blue mb-1">
                  getPreciosTraslados — Tarifas de catálogo ({numPaxs} pax)
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  {['arrival', 'departure'].map((k) => {
                    const entry = data.transfer_catalog?.[k] || {};
                    return (
                      <div key={k} className={`p-3 rounded border ${entry.found ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
                        <p className="font-bold capitalize">{k} — {entry.city || 'N/A'}</p>
                        <p className="mt-1">Found: <b>{String(entry.found)}</b></p>
                        {entry.found && (
                          <>
                            <p>Total: <b>${entry.raw?.total}</b></p>
                            <p>Per pax: <b>${entry.raw?.per_pax?.toFixed(2)}</b></p>
                            <p>Currency: {entry.raw?.currency}</p>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Decisions */}
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-pjr-blue mb-1">
                  validate_transfers() — Decisiones
                </div>
                <div className="border border-border/40 rounded divide-y">
                  <div className="p-2 bg-slate-50">
                    <p className="text-xs font-bold mb-1">Escenario 1: default (user NO modifica sector)</p>
                    <Decision label="Arrival" value={data.validate_transfers_decisions?.default_sector?.arrival} />
                    <Decision label="Departure" value={data.validate_transfers_decisions?.default_sector?.departure} />
                  </div>
                  <div className="p-2 bg-slate-50">
                    <p className="text-xs font-bold mb-1">Escenario 2: sector con mismas ciudades default</p>
                    <Decision label="Arrival" value={data.validate_transfers_decisions?.custom_sector_same_cities?.arrival} />
                    <Decision label="Departure" value={data.validate_transfers_decisions?.custom_sector_same_cities?.departure} />
                  </div>
                </div>
              </div>

              <p className="text-[10px] text-muted-foreground border-t pt-2">
                Prioridades: 1. <b>INCLUIDO</b> (Traslado='S' + default) · 2. <b>EXTRA_SOAP</b> (provider tarifa) ·
                3. <b>SIDE_PRICE</b> (sector) · 4. <b>UNAVAILABLE</b> · 5. <b>OPTIONAL_EXTRA</b> (catalog='N' pero ciudad con tarifa).
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
